import React from 'react';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { useGetBlockReleasesByNpi } from '@data/blockReleases/hooks/useGetBlockReleasesByNpi';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { useFacilitySelection } from '@components/SchedulePage/utilities/useFacilitySelection/useFacilitySelection';
import { combineConvertedReleases } from '@components/BlockRelease/utils/combineConvertedReleases';
import { ScheduleColumnData } from '@data/schedules/types/ScheduleColumnData';
import { useRouteParams } from '@components/BlockRelease/utils/hooks/useRouteParams/useRouteParams';

interface UseGetCombinedReleasesProps {
	schedules: ScheduleColumnData[];
	getBlockInfo: (npi?: string | number, unit?: string, date?: string) => void;
}

interface UseGetCombinedReleasesReturn {
	combinedReleases: BlockReleaseApiResponse[];
	setCombinedReleases: React.Dispatch<React.SetStateAction<BlockReleaseApiResponse[]>>;
}

const useFetchNpiReleases = () => {
	const { npis } = useSurgeons();
	const { getReleases, releases } = useGetBlockReleasesByNpi();

	React.useEffect(() => {
		if (npis.length > 0 && releases.length === 0) {
			getReleases({
				query: 'RELEASE_BY_NPI',
				npis,
				types: [BlockReleaseState.RECOMMENDED_RELEASE, BlockReleaseState.PENDING_RELEASE],
			});
		}
	}, [getReleases, npis, releases]);

	return { releases };
}

/**
 * @param schedules - Schedule Info for the selected release
 * @param getBlockInfo - Function to get the block info
 * This hooks takes in the schedules and fetched releases and returns the combined releases
 * This hook holds the logic for combining the fetched releases and the manual release
 */

export const useGetCombinedReleases = ({
	schedules,
	getBlockInfo,
}: UseGetCombinedReleasesProps): UseGetCombinedReleasesReturn => {
	const { getFacilityByUnit } = useFacilitySelection();
	const { releases } = useFetchNpiReleases();
	const { surgeonNpi, blockUnit, blockDate, hasAllParams } = useRouteParams();
	const [combinedReleases, setCombinedReleases] = React.useState<BlockReleaseApiResponse[]>([]);

	React.useEffect(() => {
		//If the combinedReleases are already set, do not update
		if (combinedReleases.length > 0) {
			return;
		}

		// If !hasAllParams i.e., not manual release
		if (!hasAllParams) {
			// If not manual release and no schedules i.e., no initial surgeonTimes data
			// Set the releases from the API and return
			if (schedules.length === 0) {
				setCombinedReleases(releases);
			}
			return;
		}

		// If hasAllParams i.e., manual release and schedules are empty
		// Fetch the schedules from the API
		if (schedules.length === 0) {
			getBlockInfo(surgeonNpi, blockUnit, blockDate);
			return;
		}

		const facilityInfo = getFacilityByUnit(blockUnit);
		// hospitalName: if hasAllParams i.e., manual release and schedules are fetched
		// needed hospitalTimeZone for the blockStartTime unit conversion when state is NOT_RELEASED
		const { name: hospitalName, hospitalTimeZone } = facilityInfo || {};

		// Combine the releases from the API and the manual release
		const updatedReleases = combineConvertedReleases(schedules[0], releases, surgeonNpi, hospitalName, hospitalTimeZone);

		// When the combinedReleases is not as same as releases, update the combinedReleases
		if (updatedReleases !== releases) {
			setCombinedReleases(updatedReleases);
		}
	}, [
		releases,
		schedules,
		hasAllParams,
		surgeonNpi,
		blockUnit,
		blockDate,
		combinedReleases.length,
		getBlockInfo,
		getFacilityByUnit,
	]);

	return { combinedReleases, setCombinedReleases };
};
